import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../../components/ui/table';
import { Activity } from 'lucide-react';
import { formatCurrency, formatDate, formatDateTime } from '../../../lib/utils';

export function SuperAdminAgenciesTab({ agencies }) {
    return (
        <Card className="bg-gray-800 border-border">
            <CardHeader>
                <CardTitle>Agences ({agencies.length})</CardTitle>
            </CardHeader>
            <CardContent>
                <Table>
                    <TableHeader>
                        <TableRow className="border-border">
                            <TableHead className="text-muted-foreground">Nom</TableHead>
                            <TableHead className="text-muted-foreground">Email</TableHead>
                            <TableHead className="text-muted-foreground">Ville</TableHead>
                            <TableHead className="text-muted-foreground">Utilisateurs</TableHead>
                            <TableHead className="text-muted-foreground">Mandats</TableHead>
                            <TableHead className="text-muted-foreground">Statut abo</TableHead>
                            <TableHead className="text-muted-foreground">Inscription</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {agencies.map((agency) => (
                            <TableRow key={agency.id} className="border-border">
                                <TableCell className="font-medium">{agency.name}</TableCell>
                                <TableCell className="text-foreground">{agency.email}</TableCell>
                                <TableCell className="text-foreground">{agency.city || '-'}</TableCell>
                                <TableCell>{agency.users_count}</TableCell>
                                <TableCell>{agency.mandates_count}</TableCell>
                                <TableCell>
                                    <Badge variant={agency.subscription_status === 'active' ? 'default' : 'outline'} className={agency.subscription_status === 'active' ? 'bg-green-600' : ''}>
                                        {agency.subscription_status}
                                    </Badge>
                                </TableCell>
                                <TableCell className="text-muted-foreground text-sm">
                                    {formatDate(agency.created_at)}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </CardContent>
        </Card>
    );
}

export function SuperAdminMandatesTab({ mandates }) {
    return (
        <Card className="bg-gray-800 border-border">
            <CardHeader>
                <CardTitle>Mandats ({mandates.length})</CardTitle>
            </CardHeader>
            <CardContent>
                <Table>
                    <TableHeader>
                        <TableRow className="border-border">
                            <TableHead className="text-muted-foreground">Numéro</TableHead>
                            <TableHead className="text-muted-foreground">Agence</TableHead>
                            <TableHead className="text-muted-foreground">Agent</TableHead>
                            <TableHead className="text-muted-foreground">Prix</TableHead>
                            <TableHead className="text-muted-foreground">Type</TableHead>
                            <TableHead className="text-muted-foreground">Statut</TableHead>
                            <TableHead className="text-muted-foreground">Création</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {mandates.slice(0, 50).map((mandate) => (
                            <TableRow key={mandate.id} className="border-border">
                                <TableCell className="font-mono text-sm">{mandate.mandate_number}</TableCell>
                                <TableCell className="text-foreground">{mandate.agency_name}</TableCell>
                                <TableCell className="text-foreground">{mandate.agent_name}</TableCell>
                                <TableCell>{formatCurrency(mandate.price)}</TableCell>
                                <TableCell><Badge variant="outline">{mandate.mandate_type}</Badge></TableCell>
                                <TableCell>
                                    <Badge variant={['actif', 'active', 'acknowledged', 'sent'].includes(mandate.status) ? 'default' : ['vendu', 'sold'].includes(mandate.status) ? 'secondary' : 'outline'}>
                                        {mandate.status}
                                    </Badge>
                                </TableCell>
                                <TableCell className="text-muted-foreground text-sm">{formatDate(mandate.created_at)}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </CardContent>
        </Card>
    );
}

export function SuperAdminSubscriptionsTab({ subscriptions }) {
    return (
        <Card className="bg-gray-800 border-border">
            <CardHeader>
                <CardTitle>Abonnements ({subscriptions.length})</CardTitle>
            </CardHeader>
            <CardContent>
                <Table>
                    <TableHeader>
                        <TableRow className="border-border">
                            <TableHead className="text-muted-foreground">Agence</TableHead>
                            <TableHead className="text-muted-foreground">Plan</TableHead>
                            <TableHead className="text-muted-foreground">Utilisateurs</TableHead>
                            <TableHead className="text-muted-foreground">Signatures</TableHead>
                            <TableHead className="text-muted-foreground">Coût mensuel</TableHead>
                            <TableHead className="text-muted-foreground">Statut</TableHead>
                            <TableHead className="text-muted-foreground">Fin période</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {subscriptions.map((sub) => (
                            <TableRow key={sub.id} className="border-border">
                                <TableCell className="font-medium">{sub.agency_name}</TableCell>
                                <TableCell className="capitalize">{sub.plan}</TableCell>
                                <TableCell>{sub.users_count}</TableCell>
                                <TableCell>{sub.signatures_used} / {sub.signatures_limit}</TableCell>
                                <TableCell className="font-semibold text-green-400">{formatCurrency(sub.monthly_cost)}</TableCell>
                                <TableCell>
                                    <Badge variant={sub.status === 'active' ? 'default' : 'outline'} className={sub.status === 'active' ? 'bg-green-600' : ''}>
                                        {sub.status}
                                    </Badge>
                                </TableCell>
                                <TableCell className="text-muted-foreground text-sm">{formatDate(sub.current_period_end)}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </CardContent>
        </Card>
    );
}

export function SuperAdminLogsTab({ auditLogs }) {
    return (
        <Card className="bg-gray-800 border-border">
            <CardHeader>
                <CardTitle>Activité récente</CardTitle>
            </CardHeader>
            <CardContent>
                <div className="space-y-3">
                    {auditLogs.map((log, index) => (
                        <div key={log.id || index} className="flex items-start gap-4 p-3 rounded-lg bg-gray-700/50">
                            <div className="w-10 h-10 rounded-full bg-gray-600 flex items-center justify-center">
                                <Activity className="w-5 h-5 text-muted-foreground" />
                            </div>
                            <div className="flex-1">
                                <p className="font-medium">{log.action?.replace(/_/g, ' ')}</p>
                                <p className="text-sm text-muted-foreground">
                                    {log.user_name || log.user_email || 'Système'} • {formatDateTime(log.created_at)}
                                </p>
                            </div>
                        </div>
                    ))}
                    {auditLogs.length === 0 && (
                        <p className="text-center text-muted-foreground py-8">Aucune activité récente</p>
                    )}
                </div>
            </CardContent>
        </Card>
    );
}
